import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Globe, Menu, X, Home, CreditCard, Mail, Book } from "lucide-react";

const LandingHeader: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: "Home", href: "/", icon: Home },
    { name: "Pricing", href: "/#pricing", icon: CreditCard },
    { name: 'Services', href: 'https://www.shieldtechub.com/services/', icon: Globe},
    { name: 'Blog', href: 'https://www.shieldtechub.com/blog', icon: Book},
    { name: "Contact Us", href: "/#contact-us", icon: Mail },
  ];

  const isActive = (path: string) => location.pathname === path;

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMenuOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? "bg-black/20 backdrop-blur-lg" : "bg-transparent"
      }`}
    >
      {/* Top Bar */}
      <div className="flex items-center justify-between px-4 py-4 md:px-8 md:py-6 border-b border-slate-800/30 max-w-7xl mx-auto w-full">
        {/* Logo */}
        <Link to="/" className="flex items-center -ml-12 md:ml-0 space-x-2 z-50">
          <img 
            src="/image/logo-2.png" 
            alt="ThreatInformant" 
            className="h-28 w-64 object-contain"
          />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={`text-slate-400 hover:text-white transition ${
                isActive(item.href) ? "text-white" : ""
              }`}
            >
              {item.name}
            </Link>
          ))}
        </div>

        {/* Mobile: Language + Menu Button */}
        <div className="flex md:hidden items-center gap-4 z-[1000]">
          {/* Language Button */}
          <button className="flex items-center gap-2 text-white hover:text-white transition hover:drop-shadow-[0_0_4px_rgba(255,255,255,0.7)]">
            <Globe className="w-4 h-4 drop-shadow-[0_0_1.5px_rgba(0,0,0,0.7)]" />
            <span className="drop-shadow-[0_0_1.5px_rgba(0,0,0,0.7)]">
              EN
            </span>
          </button>

          {/* Menu Toggle Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="text-whtie hover:text-white transition hover:drop-shadow-[0_0_4px_rgba(255,255,255,0.7)]"
          >
            {isMenuOpen ? (
              <X className="w-6 h-6 drop-shadow-[0_0_2px_rgba(255,255,255,0.7)]" />
            ) : (
              <Menu className="w-6 h-6 drop-shadow-[0_0_2px_rgba(255,255,255,0.7)]" />
            )}
          </button>
        </div>

        {/* Desktop CTA and Language */}
        <div className="hidden md:flex items-center gap-4">
          <button className="flex items-center gap-2 text-slate-400 hover:text-white transition">
            <Globe className="w-4 h-4" />
            <span>EN</span>
          </button>
          <Link to="/auth/login" className="px-6 py-2 inner-shadow-only">
            Get Started
          </Link>
        </div>
      </div>

      {/* Top Popup Menu (Mobile Only) */}
      <div
        className={`w-full fixed top-0 right-0 z-[100] h-screen bg-black/80 backdrop-blur-xl border-b border-slate-800/40 shadow-2xl transform transition-all duration-500 ease-[cubic-bezier(0.18,0.89,0.32,1.28)] origin-top ${
          isMenuOpen
            ? "translate-x-0 opacity-100"
            : "translate-x-full opacity-0"
        } md:hidden`}
      >
        {/* Drag Indicator */}
        <div className="w-12 h-1.5 bg-slate-600/60 rounded-full mx-auto my-3" />
        <div className="flex justify-between items-center px-6 pb-4 border-b border-slate-800/30">
          <Link to="/" className="flex items-center space-x-2">
            <img 
              src="/image/logo-2.png" 
              alt="ThreatInformant" 
              className="h-24 w-auto object-contain"
            />
          </Link>
        </div>
        <div className="flex flex-col items-start gap-6 py-6 px-6">
          {navigation.map((item, index) => (
            <Link
              key={item.name}
              to={item.href}
              onClick={() => setIsMenuOpen(false)}
              className={`flex items-center gap-3 text-slate-300 hover:text-white transition w-full py-2 text-lg ${
                isActive(item.href) ? "text-white" : ""
              }`}
              style={{
                transitionDelay: `${index * 100}ms`,
              }}
            >
              <item.icon className="w-5 h-5" />
              {item.name}
            </Link>
          ))}
        </div>
        <div className="fixed bottom-0 left-0 w-full px-6 pb-6 border-t border-slate-800/30 bg-black/80 backdrop-blur-xl z-[70] md:hidden">
          <Link
            to="/auth/login"
            onClick={() => setIsMenuOpen(false)}
            className="w-full block px-6 py-3 inner-shadow-only text-center"
          >
            Get Started
          </Link>
        </div>
      </div>

      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden transition-opacity duration-300 ${
          isMenuOpen ? "opacity-100 visible" : "opacity-0 invisible"
        }`}
        onClick={() => setIsMenuOpen(false)}
      />
    </header>
  );
};

export default LandingHeader;
